<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-container fluid class="factory-dashboard-page">
      <v-card elevation="0" class="pa-5 rounded-lg">
        <div class="d-flex align-center flex-wrap ga-2 mb-5">
          <h2 class="mb-0">{{ $t("memberDataSection.factoryDashboardPage.title") }}</h2>
          <v-spacer></v-spacer>
          <v-btn-toggle
            v-model="selectedRange"
            dense
            mandatory
            color="primary"
            class="filter-toggle"
          >
            <v-btn v-for="option in rangeOptions" :key="option.value" :value="option.value">
              {{ $t(`memberDataSection.factoryDashboardPage.ranges.${option.value}`) }}
            </v-btn>
          </v-btn-toggle>
          <v-menu v-model="dateMenu" :close-on-content-click="false" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="dateRangeLabel"
                readonly
                outlined
                dense
                hide-details
                class="date-filter"
                prepend-inner-icon="mdi-calendar-month-outline"
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker v-model="customDateRange" range>
              <v-spacer></v-spacer>
              <v-btn text color="primary" @click="dateMenu = false">{{ $t("memberDataSection.factoryDashboardPage.close") }}</v-btn>
            </v-date-picker>
          </v-menu>
          <v-btn outlined color="primary" @click="resetFilters">{{ $t("memberDataSection.factoryDashboardPage.resetFilters") }}</v-btn>
        </div>

        <v-row>
          <v-col cols="12" md="4" v-for="metric in topMetrics" :key="metric.label">
            <v-card outlined class="metric-card">
              <v-card-text class="d-flex align-center">
                <div class="metric-icon mr-3" :style="{ backgroundColor: metric.iconBg }">
                  <v-icon color="white" size="20">{{ metric.icon }}</v-icon>
                </div>
                <div>
                  <div class="metric-value" :class="{ 'metric-value--compact': metric.compact }">
                    {{ metric.value }}
                  </div>
                  <div class="metric-label">{{ metric.label }}</div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-card outlined class="mt-4 pa-4 section-card">
          <div class="section-title mb-4">{{ $t("memberDataSection.factoryDashboardPage.monthlySupplyTrend") }}</div>
          <div class="chart-wrapper">
            <apexchart type="line" height="320" :options="supplyTrendOptions" :series="supplyTrendSeries" />
          </div>
        </v-card>

        <v-card outlined class="mt-4 pa-4 section-card">
          <div class="section-title mb-4">{{ $t("memberDataSection.factoryDashboardPage.processingEfficiency") }}</div>
          <v-row>
            <v-col cols="12" lg="7">
              <div class="sub-heading mb-3">{{ $t("memberDataSection.factoryDashboardPage.funnelChart") }}</div>
              <div class="funnel-rows">
                <div
                  v-for="(item, index) in efficiencyFunnel"
                  :key="item.label"
                  class="funnel-row mb-4"
                >
                  <div class="d-flex justify-space-between mb-1">
                    <span>{{ item.label }}</span>
                    <span class="font-weight-medium">{{ item.value }}</span>
                  </div>
                  <v-progress-linear
                    :value="item.progress"
                    height="18"
                    rounded
                    :color="funnelColors[index]"
                  />
                </div>
              </div>
              <div class="text-center loss-value">{{ $t("memberDataSection.factoryDashboardPage.totalLoss") }} {{ totalLoss }}</div>
            </v-col>
            <v-col cols="12" lg="5">
              <div class="sub-heading mb-3">
                {{ $t("memberDataSection.factoryDashboardPage.operationalEfficiency") }}
                <span class="scope-hint">{{ $t("memberDataSection.factoryDashboardPage.ytdScope", { year: operationalScopeYear }) }}</span>
              </div>
              <div class="text-center chart-wrapper">
                <apexchart
                  type="radialBar"
                  height="250"
                  :options="efficiencyRadialOptions"
                  :series="[radialSeriesValue]"
                />
              </div>
              <div class="d-flex justify-space-around mt-2">
                <div class="text-center">
                  <div class="caption text-uppercase grey--text">{{ $t("memberDataSection.factoryDashboardPage.target") }}</div>
                  <template v-if="hasTarget">
                    <div class="font-weight-bold">{{ targetOperationalPercent }}%</div>
                    <div class="caption grey--text">{{ formatNumber(targetValueKg) }} kg</div>
                  </template>
                  <div v-else class="font-weight-bold grey--text">{{ $t("memberDataSection.factoryDashboardPage.noTarget") }}</div>
                </div>
                <div class="text-center">
                  <div class="caption text-uppercase grey--text">{{ $t("memberDataSection.factoryDashboardPage.actual") }}</div>
                  <div class="font-weight-bold success--text">{{ actualOperationalPercent }}%</div>
                  <div class="caption grey--text">{{ formatNumber(actualValueKg) }} kg</div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card>

        <v-card outlined class="mt-4 pa-4 section-card">
          <div class="section-title mb-4">{{ $t("memberDataSection.factoryDashboardPage.farmerParticipation") }}</div>
          <div class="chart-wrapper">
            <apexchart type="line" height="320" :options="participationOptions" :series="participationSeries" />
          </div>
        </v-card>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import Breadcrumb from "@/components/core/Breadcrumb"
import VueApexCharts from "vue-apexcharts"
import loadingMixin from "@/mixins/LoadingMixin"
export default {
  name: "FactoryDashboard",
  components: {
    breadcrumb: Breadcrumb,
    apexchart: VueApexCharts,
  },
  mixins: [loadingMixin],
  data() {
    return {
      breadcrumbs: [{ text: this.$t("memberDataSection.factoryDashboard"), disabled: true, to: "/" }],
      dateMenu: false,
      selectedRange: "month",
      rangeOptions: [{ value: "today" }, { value: "week" }, { value: "month" }, { value: "year" }, { value: "all" }],
      customDateRange: [],
      topMetrics: [
        { label: this.$t("memberDataSection.factoryDashboardPage.totalInput"), value: "0 kg", icon: "mdi-tray-arrow-down", iconBg: "#1db874" },
        { label: this.$t("memberDataSection.factoryDashboardPage.totalOutput"), value: "0 kg", icon: "mdi-cube-outline", iconBg: "#1db874" },
        { label: this.$t("memberDataSection.factoryDashboardPage.activeFarmers"), value: "0", icon: "mdi-account-group-outline", iconBg: "#1db874", compact: true },
      ],
      efficiencyFunnel: [
        { label: this.$t("memberDataSection.factoryDashboardPage.rawIntake"), value: "0 kg", progress: 0 },
        { label: this.$t("memberDataSection.factoryDashboardPage.processing"), value: "0 kg", progress: 0 },
        { label: this.$t("memberDataSection.factoryDashboardPage.output"), value: "0 kg", progress: 0 },
      ],
      funnelColors: ["#5f5f5f", "#7e9da0", "#00bd73"],
      totalLoss: "0.0%",
      hasTarget: false,
      targetOperationalPercent: 0,
      targetValueKg: 0,
      actualValueKg: 0,
      actualOperationalPercent: 0,
      operationalScopeYear: new Date().getFullYear(),
      supplyTrendSeries: [
        { name: this.$t("memberDataSection.factoryDashboardPage.currentPeriod"), data: Array(12).fill(0) },
        { name: this.$t("memberDataSection.factoryDashboardPage.previousPeriod"), data: Array(12).fill(0) },
      ],
      participationSeries: [
        { name: this.$t("memberDataSection.factoryDashboardPage.volumePerTopFarmers"), type: "column", data: [] },
        { name: this.$t("memberDataSection.factoryDashboardPage.cumulativeImpact"), type: "line", data: [] },
      ],
      participationCategories: [],
    }
  },
  mounted() {
    this.fetchDashboardData()
  },
  watch: {
    selectedRange(newVal, oldVal) {
      if (newVal === oldVal) return
      this.fetchDashboardData()
    },
    customDateRange(newVal) {
      if (!Array.isArray(newVal)) return
      if (newVal.length === 0 || newVal.length === 2) {
        if (newVal.length === 2) {
          this.dateMenu = false
        }
        this.fetchDashboardData()
      }
    },
  },
  computed: {
    // ApexCharts radialBar overflows its track past 100, so the arc is clamped
    // while the tile below keeps showing the true figure.
    radialSeriesValue() {
      return Math.max(0, Math.min(100, this.actualOperationalPercent))
    },
    dateRangeLabel() {
      if (!this.customDateRange || this.customDateRange.length < 2) return ""
      return `${this.customDateRange[0]} ${this.$t("memberDataSection.factoryDashboardPage.to")} ${this.customDateRange[1]}`
    },
    supplyTrendOptions() {
      return {
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false },
          selection: { enabled: false },
        },
        stroke: { curve: "smooth", width: [3, 2], dashArray: [0, 5] },
        colors: ["#5961f9", "#c8c8cf"],
        markers: { size: 4 },
        dataLabels: { enabled: false },
        grid: { borderColor: "#ececf2" },
        xaxis: {
          categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
        },
        yaxis: { title: { text: this.$t("memberDataSection.factoryDashboardPage.deliveries") } },
        legend: { position: "top", horizontalAlign: "right" },
      }
    },
    efficiencyRadialOptions() {
      return {
        chart: {
          sparkline: { enabled: true },
          zoom: { enabled: false },
          selection: { enabled: false },
        },
        labels: [this.$t("memberDataSection.factoryDashboardPage.operational")],
        colors: ["#00bd73"],
        plotOptions: {
          radialBar: {
            hollow: { size: "70%" },
            dataLabels: {
              name: { show: true },
              value: {
                formatter(val) {
                  return `${Math.round(val)}%`
                },
              },
            },
          },
        },
      }
    },
    participationOptions() {
      return {
        chart: {
          stacked: false,
          toolbar: { show: false },
          zoom: { enabled: false },
          selection: { enabled: false },
        },
        colors: ["#5961f9", "#d1a321"],
        plotOptions: {
          bar: {
            columnWidth: "32%",
            borderRadius: 2,
          },
        },
        dataLabels: { enabled: false },
        stroke: { width: [0, 3], curve: "smooth" },
        grid: { borderColor: "#ececf2" },
        xaxis: { categories: this.participationCategories },
        yaxis: [
          { title: { text: this.$t("memberDataSection.factoryDashboardPage.volume") } },
          { opposite: true, max: 100, title: { text: this.$t("memberDataSection.factoryDashboardPage.cumulative") } },
        ],
        legend: { position: "top", horizontalAlign: "right" },
      }
    },
  },
  methods: {
    async fetchDashboardData() {
      this.startLoading()
      try {
        const factoryId = this.$route?.params?.id
        const params = {
          range: this.selectedRange || "all",
          ...(factoryId ? { factoryId } : {}),
        }

        // Use custom date range as override when user selected it.
        if (this.customDateRange?.length === 2) {
          params.startDate = this.customDateRange[0]
          params.endDate = this.customDateRange[1]
        }

        const adminBaseUrl = localStorage.getItem("CF_BASE_URL") || process.env.VUE_APP_BASE_URL || this.$http.defaults.baseURL
        const apiBaseUrl = typeof adminBaseUrl === "string" ? adminBaseUrl.replace("/api/admin", "/api") : adminBaseUrl
        const { data } = await this.$http.get("/coffee/buying-station/factories/dashboard", {
          baseURL: apiBaseUrl,
          params,
        })
        const payload = data?.data || {}

        const summary = payload.summary || {}
        const trend = payload.monthlySupplyTrend || {}
        const efficiency = payload.processingEfficiency || {}
        const participation = payload.farmerParticipation || {}

        this.topMetrics = [
          {
            label: this.$t("memberDataSection.factoryDashboardPage.totalInput"),
            value: `${this.formatNumber(summary.totalInputKg)} kg`,
            icon: "mdi-tray-arrow-down",
            iconBg: "#1db874",
          },
          {
            label: this.$t("memberDataSection.factoryDashboardPage.totalOutput"),
            value: `${this.formatNumber(summary.totalOutputKg)} kg`,
            icon: "mdi-cube-outline",
            iconBg: "#1db874",
          },
          {
            label: this.$t("memberDataSection.factoryDashboardPage.activeFarmers"),
            value: this.formatNumber(summary.activeFarmers),
            icon: "mdi-account-group-outline",
            iconBg: "#1db874",
            compact: true,
          },
        ]

        this.supplyTrendSeries = [
          { name: this.$t("memberDataSection.factoryDashboardPage.currentPeriod"), data: trend.currentPeriod || Array(12).fill(0) },
          { name: this.$t("memberDataSection.factoryDashboardPage.previousPeriod"), data: trend.previousPeriod || Array(12).fill(0) },
        ]

        const cherryReceivedKg = Number(efficiency.cherryReceivedKg || 0)
        const cherryProcessedKg = Number(efficiency.cherryProcessedKg || 0)
        const parchmentOutKg = Number(efficiency.parchmentOutKg || 0)
        // Bars are scaled against cherry received. Parchment lands near a fifth of
        // that by physics (pulp, mucilage, water), not by underperformance.
        const funnelBase = cherryReceivedKg || 1

        this.efficiencyFunnel = [
          {
            label: this.$t("memberDataSection.factoryDashboardPage.rawIntake"),
            value: `${this.formatNumber(cherryReceivedKg)} kg`,
            progress: cherryReceivedKg ? 100 : 0,
          },
          {
            label: this.$t("memberDataSection.factoryDashboardPage.processing"),
            value: `${this.formatNumber(cherryProcessedKg)} kg`,
            progress: Math.min(100, (cherryProcessedKg / funnelBase) * 100),
          },
          {
            label: this.$t("memberDataSection.factoryDashboardPage.output"),
            value: `${this.formatNumber(parchmentOutKg)} kg`,
            progress: Math.min(100, (parchmentOutKg / funnelBase) * 100),
          },
        ]

        this.totalLoss = `${Number(efficiency.totalLossPercent || 0).toFixed(1)}%`

        this.hasTarget = Boolean(efficiency.hasTarget)
        this.targetOperationalPercent = Number(efficiency.targetOperationalPercent || 0)
        this.targetValueKg = Number(efficiency.targetValueKg || 0)
        this.actualValueKg = Number(efficiency.actualValueKg || 0)
        this.actualOperationalPercent = Number(efficiency.actualOperationalPercent || 0)
        this.operationalScopeYear = Number(efficiency.operationalScopeYear || new Date().getFullYear())

        this.participationSeries = [
          {
            name: this.$t("memberDataSection.factoryDashboardPage.volumePerTopFarmers"),
            type: "column",
            data: participation.volumePerTopFarmers || [],
          },
          {
            name: this.$t("memberDataSection.factoryDashboardPage.cumulativeImpact"),
            type: "line",
            data: participation.cumulativeImpactPercent || [],
          },
        ]

        if (Array.isArray(participation.labels) && participation.labels.length) {
          this.participationCategories = participation.labels
        }
      } catch (error) {
        console.log("factory dashboard fetch error", error)
      } finally {
        this.stopLoading()
      }
    },
    resetFilters() {
      this.customDateRange = []
      if (this.selectedRange === "month") {
        this.fetchDashboardData()
      } else {
        this.selectedRange = "month"
      }
    },
    formatNumber(val) {
      const n = Number(val || 0)
      return n.toLocaleString("en-US", { maximumFractionDigits: 0 })
    },
  },
}
</script>

<style scoped lang="scss">
.factory-dashboard-page {
  min-width: 0;

  .ga-2 {
    gap: 8px;
  }

  .filter-toggle {
    border: 1px solid #e6e6ec;
  }

  .date-filter {
    width: 230px;
  }

  .metric-card {
    border-radius: 12px !important;
    min-height: 100px;
  }

  .metric-icon {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .metric-value {
    font-size: 34px;
    line-height: 1.1;
    font-weight: 700;
  }

  .metric-value--compact {
    font-size: 30px;
    line-height: 1;
  }

  .metric-label {
    color: #6b7280;
    font-size: 14px;
  }

  .section-card {
    border-radius: 12px !important;
    overflow: hidden;
    min-width: 0;
  }

  .chart-wrapper {
    width: 100%;
    min-width: 0;
    overflow: hidden;
  }

  ::v-deep .apexcharts-canvas,
  ::v-deep .apexcharts-svg,
  ::v-deep .apexcharts-inner {
    width: 100% !important;
    max-width: 100% !important;
  }

  .section-title {
    font-size: 18px;
    font-weight: 700;
  }

  .sub-heading {
    color: #707070;
    font-size: 14px;
    font-weight: 500;
  }

  .loss-value {
    color: #ef4444;
    font-weight: 700;
    font-size: 20px;
  }

  .scope-hint {
    color: #9ca3af;
    font-size: 12px;
    font-weight: 400;
    margin-left: 4px;
  }
}
</style>
